import React, { useRef, useState } from 'react';
import ReactCrop, { centerCrop, convertToPixelCrop, } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import Button from 'src/components/10-atoms/button/Button';
import Column from 'src/components/10-atoms/layout/Column';
import Row from 'src/components/10-atoms/layout/Row';
import ModalBase from 'src/components/10-atoms/modals/ModalBase';
import OverlayLayout from 'src/components/10-atoms/overlays/OverlayLayout';
import BottomDrawerLayout from 'src/components/20-helper-views/bottom-drawer/BottomDrawerLayout';
import useTheme from 'src/customizations/themes/hooks/useTheme';
import createThemedStyle from 'src/customizations/themes/utils/createThemedStyles';
import useStableCallback from 'src/hooks/useCallbackUtils/useStableCallback';
import useI18n from 'src/modules/translations/hooks/useI18n';
import { snackbarHelperActions } from '../snackbar/useSnackbar';
import getCroppedImageFile from './utils/getCroppedImageFile';
function createInitialCrop(width, height) {
    return centerCrop({
        unit: '%',
        width: 90,
        height: 90,
    }, width, height);
}
const ImageCropSession = ({ file, imageSrc, onComplete, }) => {
    const [{ styles, theme, dimensions }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    const imageRef = useRef(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [isApplying, setIsApplying] = useState(false);
    const handleImageLoad = useStableCallback(() => {
        const image = imageRef.current;
        if (!image) {
            return;
        }
        const nextCrop = createInitialCrop(image.width, image.height);
        setCrop(nextCrop);
        setCompletedCrop(convertToPixelCrop(nextCrop, image.width, image.height));
    });
    const handleSkip = useStableCallback(() => {
        onComplete({ file, skipped: true });
    });
    const handleApply = useStableCallback(async () => {
        if (!imageRef.current || !completedCrop) {
            return;
        }
        if (!completedCrop.width || !completedCrop.height) {
            onComplete({ file, skipped: true });
            return;
        }
        try {
            setIsApplying(true);
            const croppedFile = await getCroppedImageFile(imageRef.current, completedCrop, file);
            onComplete({ file: croppedFile, skipped: false });
        }
        catch (error) {
            console.warn(error);
            snackbarHelperActions.show({
                variant: 'NEGATIVE',
                message: translate('Failed to crop the image'),
            });
        }
        finally {
            setIsApplying(false);
        }
    });
    return (React.createElement(React.Fragment, null,
        React.createElement(Column, { flex: true, xAxis: "center", yAxis: "center", style: styles.cropArea },
            React.createElement(ReactCrop, { crop: crop, keepSelection: true, onChange: nextCrop => setCrop(nextCrop), onComplete: nextCrop => setCompletedCrop(nextCrop), style: {
                    maxWidth: '100%',
                    maxHeight: dimensions.device.height * 0.55,
                } },
                React.createElement("img", { ref: imageRef, alt: translate('Image to crop'), src: imageSrc, style: {
                        maxWidth: '100%',
                        maxHeight: dimensions.device.height * 0.55,
                    }, onLoad: handleImageLoad }))),
        React.createElement(BottomDrawerLayout.Actions, null,
            React.createElement(Row, { flex: true, spacing: theme.spacing.standard.sm },
                React.createElement(Button, { flex: true, variant: "outline", onPress: handleSkip, disabled: isApplying }, translate('Skip')),
                React.createElement(Button, { flex: true, onPress: handleApply, loading: isApplying, disabled: !completedCrop?.width || !completedCrop?.height }, translate('Apply'))))));
};
const ImageCropModal = ({ visible, file, imageSrc, onClose, onComplete, }) => {
    const [{ styles, theme }] = useTheme(stylesGenerator);
    const { translate } = useI18n();
    return (React.createElement(ModalBase, { animationIn: "fadeIn", animationOut: "fadeOut", propagateSwipe: false, isVisible: visible, onDismiss: onClose, style: styles.modal },
        React.createElement(Column, { flex: true, bg: theme.palette.background.main, style: styles.container },
            React.createElement(OverlayLayout.FormHeader, { title: translate('Crop Image'), onClose: onClose }),
            visible && file && imageSrc ? (React.createElement(ImageCropSession, { key: imageSrc, file: file, imageSrc: imageSrc, onComplete: onComplete })) : undefined)));
};
const stylesGenerator = createThemedStyle(({ theme, dimensions }) => ({
    modal: {
        margin: 0,
        justifyContent: 'center',
    },
    container: {
        width: '100%',
        maxWidth: 720,
        maxHeight: dimensions.device.height * 0.92,
        alignSelf: 'center',
        borderRadius: theme.borderRadius.standard.md,
        overflow: 'hidden',
    },
    cropArea: {
        padding: theme.spacing.standard.sm,
        minHeight: 240,
        overflow: 'hidden',
    },
}));
export default ImageCropModal;
